import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Loader2, Building2, Save } from 'lucide-react';
import { apiErrorMessage } from '../../lib/apiError';
import { toast } from 'sonner';
import api from '../../lib/api';

const AdminSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    billing_email: ''
  });

  const fetchOrg = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/organization');
      if (response.data.organization === null) {
        setOrg(null);
        setLoading(false);
        return;
      }
      setOrg(response.data);
      setForm({
        name: response.data.name || '',
        description: response.data.description || '',
        billing_email: response.data.billing_email || ''
      });
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch('/admin/organization', form);
      toast.success('Organization settings updated');
      await fetchOrg();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to update settings'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500">Manage your organization settings</p>
      </div>

      {/* Organization Details */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-slate-700" />
            <CardTitle className="text-lg">Organization Details</CardTitle>
          </div>
          <CardDescription>Basic information about your organization</CardDescription>
        </CardHeader>
        <CardContent>
          {!org ? (
            <p className="text-sm text-slate-500">No organization linked to this account.</p>
          ) : (
            <form onSubmit={handleSave} className="space-y-4 max-w-lg">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organization Name</Label>
                <Input
                  id="org-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Acme Construction Inc."
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-description">Description</Label>
                <Input
                  id="org-description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="General contractor specializing in..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-billing-email">Billing Email</Label>
                <Input
                  id="org-billing-email"
                  type="email"
                  value={form.billing_email}
                  onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
                  placeholder="billing@company.com"
                />
              </div>
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Plan Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Plan</CardTitle>
          <CardDescription>Your current subscription plan</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
            <Badge variant="outline" className="shrink-0 bg-blue-50 text-blue-700 border-blue-200 text-sm px-3 py-1">
              {org?.plan ? org.plan.charAt(0).toUpperCase() + org.plan.slice(1) : 'Free'}
            </Badge>
            <span className="text-sm text-slate-500">
              Contact support to upgrade your plan
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettingsPage;
